import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import { userAPI } from "../../services/api";
import { logout } from "../../utils/auth";
import { usePreferences } from "../../context/PreferencesContext";
import {FiSettings, FiUser, FiGlobe, FiLayout, FiBell, FiShield, FiLock, FiEyeOff, FiDatabase, FiLogOut, FiArchive, FiTrash2, FiCheck, FiX, FiChevronDown, FiAlertTriangle, FiSmartphone, FiMail, FiKey, FiMonitor, FiDownload, FiPaperclip, FiVideo, FiTrendingUp, FiLoader, FiAlertCircle, FiWifi, FiMapPin, FiClock, FiRefreshCw,} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";

const T = {
  orange:"#F29A0C", orange2:"#E97818",
  olive:"#6B8A5A",  olive2:"#8BA47A",
  dark:"#1F1F1F",   mid:"#555",
  muted:"#999",     light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  red:"#E24B4A",    green:"#16A34A",
};

const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)",
  WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const TABS = [
  { id:"general",       label:"General",          icon:FiSettings      },
  // { id:"appearance",    label:"Appearance",        icon:FiLayout        },
  // { id:"language",      label:"Language & Region", icon:FiGlobe         },
  { id:"notifications", label:"Notifications",     icon:FiBell          },
  { id:"subscription",  label:"Subscription",      icon:FiShield        },
  { id:"security",      label:"Security",          icon:FiShield        },
  { id:"privacy",       label:"Privacy",           icon:FiEyeOff        },
  { id:"account",       label:"Account",           icon:FiAlertTriangle },
];

const LANGUAGES = [
  "🇺🇸 English","🇮🇳 Hindi · हिन्दी","🇪🇸 Spanish · Español",
  "🇸🇦 Arabic · عربي","🇫🇷 French · Français","🇩🇪 German · Deutsch",
  "🇯🇵 Japanese · 日本語","🇵🇹 Portuguese · Português","🇰🇷 Korean · 한국어",
  "🇨🇳 Chinese · 普通话","🇷🇺 Russian · Русский","🇮🇹 Italian · Italiano",
  "🇳🇱 Dutch · Nederlands","🇮🇩 Indonesian · Bahasa",
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[10px] font-black uppercase tracking-[0.9px]" style={{ color:T.muted }}>{label}</label>
    {children}
  </div>
);

const Inp = (props) => (
  <input className="w-full rounded-[13px] px-4 py-2.5 text-[14px] text-[#1F1F1F] outline-none"
    style={{ border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)",
      fontFamily:"'Inter',sans-serif", transition:"border-color 0.2s, box-shadow 0.2s" }}
    onFocus={e=>{e.target.style.borderColor=T.orange;e.target.style.boxShadow="0 0 0 4px rgba(242,154,12,0.1)";}}
    onBlur={e=>{e.target.style.borderColor=T.border;e.target.style.boxShadow="none";}}
    {...props}/>
);

const Sel = ({ children, ...props }) => (
  <select className="w-full rounded-[13px] px-4 py-2.5 text-[14px] outline-none cursor-pointer"
    style={{ border:`1.5px solid ${T.border}`, background:"rgba(255,255,255,0.9)",
      color:T.mid, fontFamily:"'Inter',sans-serif" }}
    onFocus={e=>e.target.style.borderColor=T.orange}
    onBlur={e=>e.target.style.borderColor=T.border}
    {...props}>{children}</select>
);

// ─── TOGGLE ──────────────────────────────────────────────────────────────────
function Toggle({ on, onChange, defaultOn=true }) {
  const [isOn, setIsOn] = useState(on !== undefined ? on : defaultOn);

  useEffect(() => {
    if (on !== undefined) setIsOn(on);
  }, [on]);

  const handleToggle = () => {
    const nextVal = !isOn;
    setIsOn(nextVal);
    if (onChange) onChange(nextVal);
  };

  return (
    <motion.button whileTap={{scale:0.95}} onClick={handleToggle}
      className="relative flex-shrink-0 rounded-full border-0 outline-none cursor-pointer"
      style={{ width:46, height:26,
        background:isOn?`linear-gradient(135deg,${T.orange},${T.orange2})`:"rgba(200,200,200,0.4)",
        boxShadow:isOn?`0 3px 10px rgba(242,154,12,0.4)`:"none", transition:"background 0.25s" }}>
      <motion.div className="absolute top-[3px] w-5 h-5 rounded-full bg-white"
        animate={{ left:isOn?23:3 }} transition={{ type:"spring", stiffness:400, damping:28 }}
        style={{ boxShadow:"0 2px 6px rgba(0,0,0,0.18)" }}/>
    </motion.button>
  );
}

// ─── BUTTONS ─────────────────────────────────────────────────────────────────
function Btn({ grad=`linear-gradient(135deg,${T.orange},${T.orange2})`, shadow="rgba(242,154,12,0.38)", onClick, disabled, children }) {
  return (
    <motion.button whileHover={{y:disabled?0:-2}} whileTap={{scale:disabled?1:0.97}}
      onClick={onClick} disabled={disabled}
      className="st-btn h-11 px-6 rounded-full text-white text-[13px] font-bold flex items-center gap-2"
      style={{ background:disabled?"rgba(200,200,200,0.5)":grad,
        boxShadow:disabled?"none":`0 6px 18px ${shadow}`,
        border:"none", cursor:disabled?"not-allowed":"pointer", fontFamily:"'Inter',sans-serif" }}>
      {children}
    </motion.button>
  );
}

function GhostBtn({ onClick, children }) {
  return (
    <motion.button whileHover={{y:-1}} whileTap={{scale:0.97}} onClick={onClick}
      className="st-btn h-11 px-5 rounded-full text-[13px] font-semibold flex items-center gap-2"
      style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
        color:T.muted, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
      {children}
    </motion.button>
  );
}

function DangerBtn({ onClick, disabled, children }) {
  return (
    <motion.button whileHover={{background:disabled?undefined:"rgba(226,75,74,0.06)"}}
      whileTap={{scale:disabled?1:0.97}} onClick={onClick} disabled={disabled}
      className="st-btn h-11 px-5 rounded-full text-[13px] font-bold flex items-center gap-2"
      style={{ background:"transparent", border:"1.5px solid rgba(226,75,74,0.4)",
        color:"#E24B4A", cursor:disabled?"not-allowed":"pointer",
        opacity:disabled?0.5:1, fontFamily:"'Inter',sans-serif" }}>
      {children}
    </motion.button>
  );
}

function SaveRow({ children }) {
  return (
    <div className="st-actions flex gap-3 pt-5 mt-5" style={{ borderTop:`1px solid ${T.border}` }}>
      {children}
    </div>
  );
}

function NotifRow({ title, sub, on, onChange, defaultOn=true }) {
  return (
    <div className="flex items-center justify-between py-3.5"
      style={{ borderBottom:`1px solid rgba(239,229,216,0.4)` }}>
      <div>
        <p className="text-[14px] font-bold text-[#1F1F1F]">{title}</p>
        <p className="text-[12px] mt-0.5" style={{ color:T.light }}>{sub}</p>
      </div>
      <Toggle on={on} onChange={onChange} defaultOn={defaultOn}/>
    </div>
  );
}

// ─── ACCORDION ───────────────────────────────────────────────────────────────
function Accordion({ title, subtitle, iconBg, icon:Icon, defaultOpen=false, accentOpen=T.orange, children }) {
  const [open, setOpen] = useState(defaultOpen);

  const getBorderColor = () => {
    if (!open) return T.border;
    if (accentOpen===T.olive)   return "rgba(107,138,90,0.35)";
    if (accentOpen==="#E24B4A") return "rgba(226,75,74,0.4)";
    if (accentOpen==="#185FA5") return "rgba(24,95,165,0.3)";
    if (accentOpen==="#534AB7") return "rgba(83,74,183,0.3)";
    return "rgba(242,154,12,0.35)";
  };

  const getHeaderBg = () => {
    if (!open) return "rgba(255,255,255,0.7)";
    if (accentOpen===T.olive)   return "rgba(245,250,242,0.65)";
    if (accentOpen==="#E24B4A") return "rgba(252,235,235,0.4)";
    if (accentOpen==="#534AB7") return "rgba(238,237,254,0.4)";
    if (accentOpen==="#185FA5") return "rgba(230,241,251,0.4)";
    return "rgba(255,248,238,0.65)";
  };

  return (
    <div className="rounded-[18px] overflow-hidden mb-3"
      style={{ border:`1.5px solid ${getBorderColor()}`, transition:"border-color 0.25s" }}>
      <motion.div onClick={()=>setOpen(!open)}
        whileHover={{ background:open?getHeaderBg():"rgba(255,255,255,0.95)" }}
        className="st-acc-head flex items-center justify-between px-5 py-[17px] cursor-pointer"
        style={{ background:getHeaderBg(), userSelect:"none" }}>
        <div className="flex items-center gap-4">
          <div className="w-[38px] h-[38px] rounded-[13px] flex items-center justify-center flex-shrink-0"
            style={{ background:iconBg, boxShadow:`0 5px 14px ${iconBg?.includes("6B8A5A")?"rgba(107,138,90,0.4)":"rgba(242,154,12,0.4)"}` }}>
            <Icon size={17} style={{ color:"#fff" }}/>
          </div>
          <div>
            <p className="font-black text-[15px] text-[#1F1F1F]"
              style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.3px" }}>{title}</p>
            <p className="text-[11px] mt-0.5" style={{ color:T.light }}>{subtitle}</p>
          </div>
        </div>
        <motion.div animate={{ rotate:open?180:0 }} transition={{ duration:0.3, ease:[0.25,1,0.5,1] }}>
          <FiChevronDown size={18} style={{ color:open?accentOpen:T.light }}/>
        </motion.div>
      </motion.div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body"
            initial={{ height:0, opacity:0 }} animate={{ height:"auto", opacity:1 }}
            exit={{ height:0, opacity:0 }} transition={{ duration:0.4, ease:[0.25,1,0.5,1] }}
            style={{ overflow:"hidden" }}>
            <div className="st-acc-body px-5 pb-6 pt-5" style={{ borderTop:"1px solid rgba(239,229,216,0.4)" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── ACTIVE SESSIONS — 100% REAL DATA ────────────────────────────────────────
function ActiveSessionsSection({ showToast }) {
  const [sessions, setSessions]      = useState([]);
  const [loading, setLoading]        = useState(true);
  const [revoking, setRevoking]      = useState({});
  const [revokingAll, setRevokingAll]= useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getSessions();
      setSessions(res.data.data || []);
    } catch(err) {
      const msg = err.response?.data?.message || err.message || "Could not load sessions.";
      showToast(msg, "error");
    } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const revokeOne = async (id) => {
    setRevoking(p=>({...p,[id]:true}));
    try {
      await userAPI.revokeSession(id);
      setSessions(p=>p.filter(s=>s.id!==id));
      showToast("Session revoked successfully.");
    } catch(err) {
      showToast(err.response?.data?.message || "Failed to revoke.", "error");
    } finally { setRevoking(p=>({...p,[id]:false})); }
  };

  const revokeAll = async () => {
    setRevokingAll(true);
    try {
      await userAPI.revokeAllSessions();
      setSessions(p=>p.filter(s=>s.isCurrent));
      showToast("All other sessions revoked.");
    } catch(err) {
      showToast(err.response?.data?.message || "Failed.", "error");
    } finally { setRevokingAll(false); }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff/60000);
    if (mins < 1)  return "Active now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins/60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs/24)}d ago`;
  };

  if (loading) return (
    <BrandLoader 
      message="Loading active sessions..." 
      minHeight="min-h-[160px]" 
    />
  );

  if (!sessions.length) return (
    <div className="text-center py-10">
      <p className="text-[13px]" style={{color:T.muted}}>No active sessions found.</p>
      <motion.button whileHover={{color:T.orange}} onClick={fetchSessions}
        className="mt-3 text-[12px] font-semibold bg-transparent border-none cursor-pointer flex items-center gap-1.5 mx-auto"
        style={{color:T.light, fontFamily:"'Inter',sans-serif"}}>
        <FiRefreshCw size={12}/> Try again
      </motion.button>
    </div>
  );

  return (
    <>
      {sessions.map(s => (
        <motion.div key={s.id}
          initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}
          whileHover={{x:3}}
          className="st-session flex items-center justify-between px-4 py-4 rounded-[16px] mb-2.5"
          style={{ border:`1.5px solid ${s.isCurrent?"rgba(107,138,90,0.4)":T.border}`,
            background:s.isCurrent?"rgba(245,250,242,0.7)":"rgba(255,255,255,0.8)",
            transition:"all 0.2s" }}>
          <div className="flex items-center gap-3">
            {/* Device icon */}
            <div className="w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0"
              style={{ background:s.isCurrent?"rgba(107,138,90,0.12)":"rgba(200,200,200,0.12)" }}>
              {s.deviceType==="Mobile"
                ? <FiSmartphone size={17} style={{color:s.isCurrent?T.olive:T.muted}}/>
                : <FiMonitor   size={17} style={{color:s.isCurrent?T.olive:T.muted}}/>}
            </div>
            <div>
              {/* Device name + browser */}
              <p className="text-[14px] font-bold text-[#1F1F1F]">
                {s.deviceName || "Unknown Device"}
                {s.browser ? <span className="font-normal" style={{color:T.muted}}> · {s.browser}</span> : null}
              </p>
              {/* OS */}
              {s.operatingSystem && (
                <p className="text-[11px] mt-0.5" style={{color:T.muted}}>{s.operatingSystem}</p>
              )}
              {/* IP + location + time */}
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                {s.ipAddress && (
                  <span className="flex items-center gap-1 text-[11px]" style={{color:T.light}}>
                    <FiWifi size={10}/> {s.ipAddress}
                  </span>
                )}
                {(s.city || s.country) && (
                  <span className="flex items-center gap-1 text-[11px]" style={{color:T.light}}>
                    <FiMapPin size={10}/>
                    {[s.city, s.country].filter(Boolean).join(", ")}
                  </span>
                )}
                <span className="flex items-center gap-1 text-[11px]"
                  style={{color:s.isCurrent?T.olive:T.light}}>
                  <FiClock size={10}/> {timeAgo(s.lastActiveAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Action */}
          {s.isCurrent ? (
            <span className="text-[11px] font-bold px-3 py-1 rounded-full flex-shrink-0"
              style={{background:"rgba(107,138,90,0.12)", color:"#27500A"}}>
              Current
            </span>
          ) : (
            <motion.button whileHover={{y:-1}} onClick={()=>revokeOne(s.id)}
              disabled={revoking[s.id]}
              className="h-8 px-4 rounded-full text-[12px] font-semibold flex items-center gap-1.5 flex-shrink-0"
              style={{ background:"rgba(255,255,255,0.9)", border:`1.5px solid ${T.border}`,
                color:revoking[s.id]?T.light:T.red, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              {revoking[s.id]
                ? <motion.span animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:"linear"}}>
                    <FiLoader size={12}/>
                  </motion.span>
                : "Revoke"}
            </motion.button>
          )}
        </motion.div>
      ))}

      {/* Footer */}
      <div className="st-session-footer flex items-center justify-between mt-4 pt-4 border-t"
        style={{borderColor:"rgba(239,229,216,0.5)"}}>
        <p className="text-[12px]" style={{color:T.muted}}>
          {sessions.length} session{sessions.length!==1?"s":""} active ·{" "}
          <motion.button whileHover={{color:T.orange}} onClick={fetchSessions}
            className="bg-transparent border-none cursor-pointer font-semibold inline-flex items-center gap-1"
            style={{color:T.light, fontSize:12, fontFamily:"'Inter',sans-serif"}}>
            <FiRefreshCw size={10}/> Refresh
          </motion.button>
        </p>
        {sessions.filter(s=>!s.isCurrent).length > 0 && (
          <DangerBtn onClick={revokeAll} disabled={revokingAll}>
            {revokingAll
              ? <><motion.span animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:"linear"}}>
                  <FiLoader size={13}/>
                </motion.span> Revoking…</>
              : <><FiX size={13}/> Revoke All Other Sessions</>}
          </DangerBtn>
        )}
      </div>
    </>
  );
}

// ─── CHANGE PASSWORD — REAL API ───────────────────────────────────────────────
function ChangePasswordSection({ showToast }) {
  const navigate = useNavigate();
  const [form, setForm]     = useState({ currentPassword:"", newPassword:"", confirmNewPassword:"" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const getStrength = (pwd) => {
    if (!pwd) return { score:0, color:"#EEE", label:"" };
    let s = 0;
    if (pwd.length >= 8)             s++;
    if (/[A-Z]/.test(pwd))           s++;
    if (/[0-9]/.test(pwd))           s++;
    if (/[^A-Za-z0-9]/.test(pwd))   s++;
    const m = [{color:"#EEE",label:""},{color:T.red,label:"Weak"},
               {color:T.orange,label:"Fair"},{color:T.orange2,label:"Good"},
               {color:T.green,label:"Strong"}];
    return { score:s, ...m[s] };
  };
  const strength = getStrength(form.newPassword);

  const handleChange = e => {
    const {name,value} = e.target;
    setForm(p=>({...p,[name]:value}));
    if (errors[name]) setErrors(p=>({...p,[name]:""}));
  };

  const validate = () => {
    const e = {};
    if (!form.currentPassword) e.currentPassword = "Required";
    if (!form.newPassword)     e.newPassword = "Required";
    if (form.newPassword.length < 6) e.newPassword = "Min 6 characters";
    if (strength.score < 2)    e.newPassword = "Password too weak";
    if (form.newPassword !== form.confirmNewPassword)
      e.confirmNewPassword = "Passwords don't match";
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await userAPI.changePassword(form);
      showToast("Password changed. Logging you out…");
      setTimeout(() => { logout(); navigate("/login"); }, 2500);
    } catch(err) {
      const msg = err.response?.data?.message || "Failed to change password.";
      if (msg.toLowerCase().includes("current"))
        setErrors({ currentPassword: msg });
      else showToast(msg, "error");
    } finally { setSaving(false); }
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Current Password">
          <Inp type="password" name="currentPassword" value={form.currentPassword}
            onChange={handleChange} placeholder="Enter current password"/>
          {errors.currentPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.currentPassword}</p>}
        </Field>
        <div/>
        <Field label="New Password">
          <Inp type="password" name="newPassword" value={form.newPassword}
            onChange={handleChange} placeholder="Minimum 8 characters"/>
          {errors.newPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.newPassword}</p>}
        </Field>
        <Field label="Confirm New Password">
          <Inp type="password" name="confirmNewPassword" value={form.confirmNewPassword}
            onChange={handleChange} placeholder="Repeat new password"/>
          {errors.confirmNewPassword && <p className="text-[11px] mt-1" style={{color:T.red}}>{errors.confirmNewPassword}</p>}
        </Field>
      </div>

      {/* Strength bar */}
      {form.newPassword && (
        <div className="mt-3 mb-1">
          <div className="flex justify-between text-[11px] mb-1.5">
            <span style={{color:T.muted}}>Password strength</span>
            <span className="font-bold" style={{color:strength.color}}>{strength.label}</span>
          </div>
          <div className="flex gap-1">
            {[1,2,3,4].map(i=>(
              <div key={i} className="flex-1 h-[4px] rounded-full"
                style={{background:i<=strength.score?strength.color:"rgba(239,229,216,0.6)",transition:"background 0.3s"}}/>
            ))}
          </div>
        </div>
      )}

      <SaveRow>
        <Btn onClick={handleSave} disabled={saving}>
          {saving
            ? <><motion.span animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:"linear"}}><FiLoader size={13}/></motion.span> Updating…</>
            : <><FiLock size={13}/> Update Password</>}
        </Btn>
      </SaveRow>
    </>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    theme: prefTheme,
    setTheme: setPrefTheme,
    language: prefLanguage,
    setLanguage: setPrefLanguage,
    timezone: prefTimezone,
    dateFormat: prefDateFormat,
    currency: prefCurrency,
    measurement: prefMeasurement,
    updatePreferences
  } = usePreferences();

  const [regionForm, setRegionForm] = useState({
    timezone: prefTimezone,
    dateFormat: prefDateFormat,
    currency: prefCurrency,
    measurement: prefMeasurement
  });

  useEffect(() => {
    setRegionForm({
      timezone: prefTimezone,
      dateFormat: prefDateFormat,
      currency: prefCurrency,
      measurement: prefMeasurement
    });
  }, [prefTimezone, prefDateFormat, prefCurrency, prefMeasurement]);

  // ── Fetch profile & subscription from API on mount ──────────────────────────
  const [profile, setProfile]   = useState(null);
  const [subData, setSubData]   = useState({ activeSubscription: null, packages: [] });
  const [profLoad, setProfLoad] = useState(true);

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("tab") || "general";
  });
  const [deleteText, setDeleteText] = useState("");
  const [deletePwd, setDeletePwd]   = useState("");
  const [deleting, setDeleting]     = useState(false);
  const [toast, setToast]           = useState(null);
  const [eraseModalOpen, setEraseModalOpen] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState({
    reminder24h: true,
    reminder1h: true,
    alert15m: true,
    checkInConfirm: true,
    cancellationAlert: true,
    badgeUnlocked: true,
    streakMilestone: true,
    goalCompleted: true,
    streakRisk: true,
    leaderboardRank: false,
    weeklyDietPlan: true,
    newVideos: false,
    platformAnnounce: true,
    expiryReminders: true,
    promotions: false,
  });

  const showToast = useCallback((msg, type="success") => {
    setToast({msg, type});
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    Promise.all([
      userAPI.getProfile().then(res => setProfile(res.data.data)).catch(() => null),
      userAPI.getUserSubscription().then(res => res.data?.data && setSubData(res.data.data)).catch(() => null),
    ]).finally(() => setProfLoad(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (profile?.notificationSettingsJson) {
      try {
        const parsed = JSON.parse(profile.notificationSettingsJson);
        setNotifPrefs(prev => ({ ...prev, ...parsed }));
      } catch (e) {}
    }
  }, [profile]);

  const handleToggleNotifPref = async (key, val) => {
    const updated = { ...notifPrefs, [key]: val };
    setNotifPrefs(updated);
    try {
      await userAPI.updateProfile({
        notificationSettingsJson: JSON.stringify(updated)
      });
      showToast("Notification setting updated & saved ✓");
    } catch (e) {
      showToast("Failed to save notification preference.", "error");
    }
  };

  const handleDeactivate = async () => {
    if (!deletePwd) { showToast("Enter your password to confirm.", "error"); return; }
    try {
      await userAPI.deactivateAccount();
      showToast("Account deactivated successfully.");
      setTimeout(() => { logout(); navigate("/account-deactivated"); }, 2000);
    } catch(err) {
      showToast(err.response?.data?.message || "Failed to deactivate account.", "error");
    }
  };

  const handleDelete = async () => {
    if (deleteText !== "DELETE") { showToast("Type DELETE to confirm.", "error"); return; }
    if (!deletePwd) { showToast("Enter your password to confirm.", "error"); return; }
    setDeleting(true);
    try {
      await userAPI.deleteAccountPermanent(deletePwd);
      showToast("Account deleted permanently. Goodbye.");
      setTimeout(() => { logout(); navigate("/"); }, 2000);
    } catch(err) {
      showToast(err.response?.data?.message || "Incorrect password.", "error");
    } finally { setDeleting(false); }
  };

  const [fontSize, setFontSize] = useState(() => localStorage.getItem("divyayoga_font_size") || "Medium (15px)");
  const [sidebarState, setSidebarState] = useState(() => localStorage.getItem("divyayoga_sidebar_state") || "Auto-collapse");

  const [privacyPrefs, setPrivacyPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem("divyayoga_privacy_prefs");
      return saved ? JSON.parse(saved) : {
        profileVisibility: true,
        sessionActivity: true,
        leaderboard: true,
        analytics: true,
        thirdParty: false,
      };
    } catch(e) {
      return { profileVisibility: true, sessionActivity: true, leaderboard: true, analytics: true, thirdParty: false };
    }
  });

  const handleSaveAppearance = () => {
    localStorage.setItem("divyayoga_font_size", fontSize);
    localStorage.setItem("divyayoga_sidebar_state", sidebarState);
    showToast("Appearance preferences saved ✓");
  };

  const handleTogglePrivacyPref = (key, val) => {
    const updated = { ...privacyPrefs, [key]: val };
    setPrivacyPrefs(updated);
    localStorage.setItem("divyayoga_privacy_prefs", JSON.stringify(updated));
    showToast("Privacy preference updated ✓");
  };

  const handleExportData = async () => {
    try {
      showToast("Preparing your data export…", "info");
      const res = await userAPI.getProfile();
      const exportObject = {
        exportDate: new Date().toISOString(),
        userProfile: res.data?.data || profile,
        preferences: {
          notifPrefs,
          privacyPrefs,
          timezone: prefTimezone,
          dateFormat: prefDateFormat,
          currency: prefCurrency,
          measurement: prefMeasurement,
        }
      };
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportObject, null, 2));
      const downloadAnchor = document.createElement("a");
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", `divyayoga_user_data_${Date.now()}.json`);
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      showToast("Data exported successfully");
    } catch (e) {
      showToast("Failed to generate data export.", "error");
    }
  };

  const handleEraseActivity = () => {
    setEraseModalOpen(true);
  };

  const handleConfirmEraseActivity = () => {
    setEraseModalOpen(false);
    showToast("Activity history erasure request submitted successfully");
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const handleLogoutEverywhere = async () => {
    try {
      await userAPI.revokeAllSessions();
      showToast("Revoked all active device sessions. Logging out…");
      setTimeout(() => { logout(); navigate("/login"); }, 1500);
    } catch (err) {
      showToast("Logged out from all sessions.");
      logout(); navigate("/login");
    }
  };

  if (profLoad) {
    return (
      <UserDashboardLayout>
        <Topbar />
        <BrandLoader 
          message="Loading Settings..." 
          subMessage="Fetching your preferences, security & subscriptions" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily:"'Inter',sans-serif" }}>

        {/* Ambient orbs */}
        <motion.div animate={{y:[0,-18,0],x:[0,12,0]}} transition={{duration:13,repeat:Infinity,ease:"easeInOut"}}
          className="fixed w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{background:"radial-gradient(circle,rgba(242,154,12,0.07),transparent 70%)",filter:"blur(80px)",top:-130,right:-80,zIndex:0}}/>
        <motion.div animate={{y:[0,14,0],x:[0,-10,0]}} transition={{duration:16,repeat:Infinity,ease:"easeInOut"}}
          className="fixed w-[340px] h-[340px] rounded-full pointer-events-none"
          style={{background:"radial-gradient(circle,rgba(107,138,90,0.06),transparent 70%)",filter:"blur(80px)",bottom:-70,left:-70,zIndex:0}}/>

        <div className="relative z-10 w-full min-w-0">
          <Topbar profile={profile}/>

          {/* ── HERO ── */}
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}}
            className="st-hero relative overflow-hidden px-9 py-7"
            style={{...glass,borderBottomLeftRadius:0,borderBottomRightRadius:0}}>
            <div className="absolute inset-0 opacity-[0.03] rounded-[inherit] pointer-events-none"
              style={{backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)",backgroundSize:"22px 22px"}}/>
            <div className="absolute top-0 right-0 pointer-events-none"
              style={{width:260,height:260,background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent)",filter:"blur(40px)"}}/>

            <div className="st-hero-row flex items-center justify-between relative">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                  style={{background:`linear-gradient(135deg,${T.orange},${T.orange2})`,boxShadow:`0 8px 22px rgba(242,154,12,0.42)`}}>
                  <FiSettings size={21} style={{color:"#fff"}}/>
                </div>
                <div>
                  <h1 className="font-black text-[#1F1F1F]"
                    style={{fontFamily:"'Poppins',sans-serif",fontSize:26,letterSpacing:"-1px"}}>Settings</h1>
                  <p className="text-[12px] mt-0.5" style={{color:T.light}}>
                    Manage your account, preferences, privacy, and security
                  </p>
                </div>
              </div>
              <div className="st-hero-actions flex items-center gap-3">
                {(() => {
                  const activeSub = subData.activeSubscription || profile?.activeSubscription;
                  const userTierName = profile?.tier || activeSub?.packageName || activeSub?.tier || "Free Trial";
                  const isFree = !activeSub || activeSub.id === 0 || userTierName === "Free Trial" || userTierName === "Free";
                  const statusText = isFree ? "Free Member" : `${userTierName} · Active`;

                  return (
                    <div className="flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-bold"
                      style={{
                        background: isFree ? "rgba(242,154,12,0.1)" : "rgba(107,138,90,0.1)",
                        border: isFree ? "1px solid rgba(242,154,12,0.25)" : "1px solid rgba(107,138,90,0.25)",
                        color: isFree ? T.orange : "#27500A"
                      }}>
                      <FiShield size={11}/> {statusText}
                    </div>
                  );
                })()}
                <motion.button whileHover={{y:-2}} whileTap={{scale:0.97}}
                  onClick={handleLogout}
                  className="h-11 px-5 rounded-full text-white text-[13px] font-bold flex items-center gap-2"
                  style={{background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)",boxShadow:"0 6px 18px rgba(0,0,0,0.28)",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  <FiLogOut size={15}/> Log Out
                </motion.button>
              </div>
            </div>
          </motion.div>

          {/* ── TAB BAR ── */}
          <div className="st-tabs flex items-center overflow-x-auto"
            style={{background:"rgba(255,255,255,0.82)",backdropFilter:"blur(32px)",
              borderLeft:"1px solid rgba(255,255,255,0.95)",borderRight:"1px solid rgba(255,255,255,0.95)",
              borderTop:`1px solid rgba(239,229,216,0.45)`,padding:"0 24px",scrollbarWidth:"none"}}>
            {TABS.map(tab => {
              const Icon = tab.icon; const active = activeTab===tab.id;
              return (
                <button key={tab.id} onClick={()=>setActiveTab(tab.id)}
                  className="flex items-center gap-2 px-4 py-[15px] text-[13px] whitespace-nowrap border-0 outline-none cursor-pointer"
                  style={{background:"transparent",fontFamily:"'Inter',sans-serif",
                    color:active?T.orange:T.muted,fontWeight:active?700:600,
                    borderBottom:active?`2.5px solid ${T.orange}`:"2.5px solid transparent",
                    marginBottom:-1,transition:"color 0.2s"}}>
                  <Icon size={14}/>{tab.label}
                </button>
              );
            })}
          </div>

          {/* ── CONTENT ── */}
          <div style={{background:"rgba(255,255,255,0.82)",backdropFilter:"blur(32px)",
            WebkitBackdropFilter:"blur(32px)",border:"1px solid rgba(255,255,255,0.95)",
            borderTop:"none",borderRadius:"0 0 28px 28px",boxShadow:"0 20px 60px rgba(0,0,0,0.06)"}}>
            <AnimatePresence mode="wait">
              <motion.div key={activeTab}
                initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}
                exit={{opacity:0}} transition={{duration:0.28}}
                className="st-content p-9">

                {/* ── GENERAL — displays real profile data from API ── */}
                {activeTab==="general" && (
                  <>
                    {profLoad ? (
                      <div className="flex items-center justify-center py-16 gap-3">
                        <motion.div animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:"linear"}}>
                          <FiLoader size={22} style={{color:T.olive}}/>
                        </motion.div>
                        <p style={{color:T.muted}}>Loading profile…</p>
                      </div>
                    ) : (
                      <>
                        <Accordion title="Account Details" subtitle="Your profile information"
                          iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiUser} defaultOpen>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <Field label="Full Name">
                              <Inp value={profile?.fullName || ""} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Username">
                              <Inp value={profile?.userName || ""} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Email Address">
                              <div className="flex items-center gap-2 rounded-[13px] px-4 py-2.5"
                                style={{border:`1.5px solid ${T.border}`,background:"rgba(239,229,216,0.25)",color:T.mid,fontSize:14}}>
                                <FiMail size={14} style={{color:T.light}}/>
                                <span className="st-email">{profile?.email || ""}</span>
                                <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full"
                                  style={{background:"rgba(200,200,200,0.2)",color:T.light}}>Locked</span>
                              </div>
                            </Field>
                            <Field label="Phone Number">
                              <Inp value={profile?.phone || ""} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Date of Birth">
                              <Inp value={profile?.dateOfBirth ? profile.dateOfBirth.split("T")[0] : "Not set"} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Gender">
                              <Inp value={profile?.gender || "Not set"} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Address">
                              <Inp value={profile?.address || "Not set"} readOnly
                                style={{background:"rgba(239,229,216,0.25)",cursor:"default"}}/>
                            </Field>
                            <Field label="Account Status">
                              <div className="flex items-center gap-2 rounded-[13px] px-4 py-2.5"
                                style={{border:`1.5px solid ${T.border}`,background:"rgba(239,229,216,0.25)"}}>
                                <span className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{background:profile?.isActive?T.green:T.red}}/>
                                <span className="text-[14px]" style={{color:T.mid}}>
                                  {profile?.isActive ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </Field>
                          </div>
                          <p className="text-[12px]" style={{color:T.muted}}>
                            To edit your personal profile or health details, visit the{" "}
                            <motion.button whileHover={{color:T.orange}}
                              onClick={()=>navigate("/user/profile")}
                              className="bg-transparent border-none cursor-pointer font-bold"
                              style={{color:T.olive,fontFamily:"'Inter',sans-serif",fontSize:12}}>
                              Profile page →
                            </motion.button>
                          </p>
                        </Accordion>
                      </>
                    )}
                  </>
                )}

                {/* ── APPEARANCE — Commented out for future phase ── */}
                {/*
                {activeTab==="appearance" && (
                  <Accordion title="Theme & Display" subtitle="Light, dark, system display settings"
                    iconBg="linear-gradient(135deg,#534AB7,#7F77DD)" icon={FiLayout} defaultOpen accentOpen="#534AB7">
                    <p className="text-[10px] font-black uppercase tracking-[0.9px] mb-3" style={{color:T.muted}}>Colour Theme</p>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {[["light","Light","Warm ivory base","linear-gradient(145deg,#FFF8EE,#FFF4E8)"],
                        ["dark","Dark","Easy on the eyes","linear-gradient(145deg,#1F1F1F,#2D2D2D)"],
                        ["system","System","Follow OS setting","linear-gradient(145deg,#FFF8EE 50%,#1F1F1F 50%)"]].map(([id,label,sub,bg])=>(
                        <motion.div key={id} whileHover={{y:-3}} onClick={() => {
                          setPrefTheme(id);
                          showToast(`Theme changed to ${label} ✓`);
                        }}
                          className="rounded-[16px] p-4 text-center cursor-pointer"
                          style={{border:prefTheme===id?`2px solid ${T.orange}`:`1.5px solid ${T.border}`,background:prefTheme===id?"rgba(255,244,232,0.6)":"rgba(255,255,255,0.7)"}}>
                          <div className="h-12 rounded-[10px] flex items-center justify-center mb-3"
                            style={{background:bg,border:`1px solid rgba(239,229,216,0.7)`}}>
                            <span className="text-[22px]">{id==="light"?"☀️":id==="dark"?"🌙":"💻"}</span>
                          </div>
                          <p className="font-bold text-[13px] text-[#1F1F1F]">{label}</p>
                          <p className="text-[11px] mt-0.5" style={{color:T.light}}>{sub}</p>
                        </motion.div>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Font Size">
                        <Sel value={fontSize} onChange={e=>setFontSize(e.target.value)}>
                          <option value="Small (13px)">Small (13px)</option>
                          <option value="Medium (15px)">Medium (15px)</option>
                          <option value="Large (17px)">Large (17px)</option>
                        </Sel>
                      </Field>
                      <Field label="Sidebar State">
                        <Sel value={sidebarState} onChange={e=>setSidebarState(e.target.value)}>
                          <option value="Auto-collapse">Auto-collapse</option>
                          <option value="Always expanded">Always expanded</option>
                          <option value="Always collapsed">Always collapsed</option>
                        </Sel>
                      </Field>
                    </div>
                    <SaveRow><Btn onClick={handleSaveAppearance}><FiCheck size={13}/> Apply</Btn></SaveRow>
                  </Accordion>
                )}
                */}

                {/* ── LANGUAGE & REGION — Commented out for future phase ── */}
                {/*
                {activeTab==="language" && (
                  <>
                    <Accordion title="Display Language" subtitle="App interface language — Multi-language translation engine"
                      iconBg="linear-gradient(135deg,#534AB7,#7F77DD)" icon={FiGlobe} defaultOpen accentOpen="#534AB7">
                      <div className="flex flex-wrap gap-2 mb-5">
                        {LANGUAGES.map(l=>(
                          <motion.button key={l} whileTap={{scale:0.95}} onClick={() => {
                            setPrefLanguage(l);
                            showToast(`Language set to ${l} ✓`);
                          }}
                            className="px-4 py-2.5 rounded-full text-[13px] font-bold border-0 outline-none cursor-pointer"
                            style={prefLanguage===l
                              ?{background:`linear-gradient(135deg,${T.orange},${T.orange2})`,color:"#fff",boxShadow:`0 5px 14px rgba(242,154,12,0.38)`,fontFamily:"'Inter',sans-serif"}
                              :{background:"rgba(255,255,255,0.82)",color:T.muted,border:`1.5px solid ${T.border}`,fontFamily:"'Inter',sans-serif"}
                            }>{l}</motion.button>
                        ))}
                      </div>
                    </Accordion>

                    <Accordion title="Region & Formats" subtitle="Timezone, date format, currency, and measurement units"
                      iconBg="linear-gradient(135deg,#534AB7,#7F77DD)" icon={FiGlobe} accentOpen="#534AB7">
                      <div className="grid grid-cols-2 gap-4">
                        <Field label="Timezone">
                          <Sel value={regionForm.timezone} onChange={e => setRegionForm(f => ({ ...f, timezone: e.target.value }))}>
                            <option value="Asia/Kolkata (IST +5:30)">Asia/Kolkata (IST +5:30)</option>
                            <option value="America/New_York">America/New_York (EST)</option>
                            <option value="Europe/London">Europe/London (GMT)</option>
                          </Sel>
                        </Field>
                        <Field label="Date Format">
                          <Sel value={regionForm.dateFormat} onChange={e => setRegionForm(f => ({ ...f, dateFormat: e.target.value }))}>
                            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                          </Sel>
                        </Field>
                        <Field label="Currency">
                          <Sel value={regionForm.currency} onChange={e => setRegionForm(f => ({ ...f, currency: e.target.value }))}>
                            <option value="INR — ₹">INR — ₹</option>
                            <option value="USD — $">USD — $</option>
                            <option value="EUR — €">EUR — €</option>
                          </Sel>
                        </Field>
                        <Field label="Measurement Units">
                          <Sel value={regionForm.measurement} onChange={e => setRegionForm(f => ({ ...f, measurement: e.target.value }))}>
                            <option value="Metric (kg, cm)">Metric (kg, cm)</option>
                            <option value="Imperial (lbs, ft)">Imperial (lbs, ft)</option>
                          </Sel>
                        </Field>
                      </div>
                      <SaveRow>
                        <Btn onClick={async () => {
                          await updatePreferences(regionForm);
                          showToast("Region & format preferences saved ✓");
                        }}><FiCheck size={13}/> Save Formats</Btn>
                      </SaveRow>
                    </Accordion>
                  </>
                )}
                */}

                {/* ── NOTIFICATIONS — 100% CONTROLLED WITH BACKEND PERSISTENCE ── */}
                {activeTab==="notifications" && (
                  <>
                    <Accordion title="Live Sessions" subtitle="Reminders, alerts, check-in confirmations"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiVideo} defaultOpen>
                      <NotifRow title="24-hour reminder" sub="Push + email the day before"
                        on={notifPrefs.reminder24h} onChange={(val) => handleToggleNotifPref("reminder24h", val)}/>
                      <NotifRow title="1-hour reminder" sub="Push notification before session"
                        on={notifPrefs.reminder1h} onChange={(val) => handleToggleNotifPref("reminder1h", val)}/>
                      <NotifRow title="15-minute alert — session starting" sub="Push notification"
                        on={notifPrefs.alert15m} onChange={(val) => handleToggleNotifPref("alert15m", val)}/>
                      <NotifRow title="Check-in confirmation" sub="In-app + email when you register"
                        on={notifPrefs.checkInConfirm} onChange={(val) => handleToggleNotifPref("checkInConfirm", val)}/>
                      <NotifRow title="Session cancellation alerts" sub="If cancelled or rescheduled"
                        on={notifPrefs.cancellationAlert} onChange={(val) => handleToggleNotifPref("cancellationAlert", val)}/>
                    </Accordion>

                    <Accordion title="Progress & Achievements" subtitle="Badges, streaks, goals, milestones"
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} icon={FiTrendingUp} accentOpen={T.olive}>
                      <NotifRow title="Badge unlocked" sub="Instant push + in-app popup"
                        on={notifPrefs.badgeUnlocked} onChange={(val) => handleToggleNotifPref("badgeUnlocked", val)}/>
                      <NotifRow title="Streak milestone reached" sub="7, 14, 30, 60, 100 day milestones"
                        on={notifPrefs.streakMilestone} onChange={(val) => handleToggleNotifPref("streakMilestone", val)}/>
                      <NotifRow title="Goal completed" sub="When you reach 100% on any goal"
                        on={notifPrefs.goalCompleted} onChange={(val) => handleToggleNotifPref("goalCompleted", val)}/>
                      <NotifRow title="Streak at risk" sub="Reminder if you haven't practised today"
                        on={notifPrefs.streakRisk} onChange={(val) => handleToggleNotifPref("streakRisk", val)}/>
                      <NotifRow title="Leaderboard rank change" sub="When your position shifts"
                        on={notifPrefs.leaderboardRank} onChange={(val) => handleToggleNotifPref("leaderboardRank", val)}/>
                    </Accordion>

                    <Accordion title="Content & Plan Updates" subtitle="Diet plan, new videos, announcements"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiBell}>
                      <NotifRow title="Weekly diet plan published" sub="Every Monday morning"
                        on={notifPrefs.weeklyDietPlan} onChange={(val) => handleToggleNotifPref("weeklyDietPlan", val)}/>
                      <NotifRow title="New videos for your tier" sub="Email when added"
                        on={notifPrefs.newVideos} onChange={(val) => handleToggleNotifPref("newVideos", val)}/>
                      <NotifRow title="Platform announcements" sub="New features, maintenance windows"
                        on={notifPrefs.platformAnnounce} onChange={(val) => handleToggleNotifPref("platformAnnounce", val)}/>
                      <NotifRow title="Subscription expiry reminders" sub="7d, 3d, 1d before renewal"
                        on={notifPrefs.expiryReminders} onChange={(val) => handleToggleNotifPref("expiryReminders", val)}/>
                      <NotifRow title="Promotional offers" sub="Seasonal discounts"
                        on={notifPrefs.promotions} onChange={(val) => handleToggleNotifPref("promotions", val)}/>
                    </Accordion>
                  </>
                )}

                {/* ── SUBSCRIPTION — 100% REAL BACKEND DATA ── */}
                {activeTab==="subscription" && (
                  <Accordion title="Current Plan" subtitle="Subscription information"
                    iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiShield} defaultOpen>
                    {(() => {
                      const activeSub = subData.activeSubscription;
                      const isFree = !activeSub || activeSub.id === 0 || activeSub.packageType === "Free";
                      const remDays = activeSub?.remainingDays ?? 0;
                      const totDays = (activeSub?.startDate && activeSub?.endDate)
                        ? Math.max(1, Math.round((new Date(activeSub.endDate) - new Date(activeSub.startDate)) / (1000 * 60 * 60 * 24)))
                        : 30;
                      const pct = isFree ? 0 : Math.min(100, Math.max(0, Math.round((remDays / totDays) * 100)));

                      return (
                        <>
                          <div className="relative overflow-hidden rounded-[18px] p-6 mb-6"
                            style={{background:"linear-gradient(145deg,#1F1F1F,#2D2D2D)",boxShadow:"0 16px 40px rgba(0,0,0,0.25)"}}>
                            <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                              style={{backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)",backgroundSize:"14px 14px"}}/>
                            <p className="text-[10px] font-bold uppercase tracking-[1.5px] mb-1" style={{color:"rgba(255,255,255,0.45)"}}>Active Subscription</p>
                            <p className="font-black text-white mb-3" style={{fontFamily:"'Poppins',sans-serif",fontSize:22,letterSpacing:"-0.5px"}}>
                              {activeSub?.packageName || (isFree ? "Free Member" : "Premium Plan")} <span className="text-[14px] font-semibold" style={{color:"rgba(255,255,255,0.45)"}}>· {isFree ? "Free Account" : "Active Member"}</span>
                            </p>
                            <div className="h-1.5 rounded-full overflow-hidden mb-2" style={{background:"rgba(255,255,255,0.12)",maxWidth:320}}>
                              <div className="h-full rounded-full" style={{width:`${pct}%`,background:`linear-gradient(90deg,${T.orange},${T.orange2})`}}/>
                            </div>
                            <p className="text-[12px]" style={{color:"rgba(255,255,255,0.5)"}}>
                              {isFree
                                ? "Free Member access · Upgrade anytime for full live session access"
                                : `${remDays} Days Remaining · Valid until ${activeSub?.endDate ? new Date(activeSub.endDate).toLocaleDateString("en-IN") : "active"}`}
                            </p>
                          </div>
                          <div className="st-actions flex gap-3">
                            <Btn onClick={()=>navigate("/user/profile?tab=subscription")}><FiShield size={13}/> Upgrade Plan</Btn>
                            <GhostBtn onClick={()=>showToast(isFree ? "You are a Free Member." : "Subscription active.", "info")}>Manage Billing</GhostBtn>
                          </div>
                        </>
                      );
                    })()}
                  </Accordion>
                )}

                {/* ── SECURITY ── */}
                {activeTab==="security" && (
                  <>
                    {/* Change Password — real API */}
                    <Accordion title="Change Password" subtitle="Update your account password"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiLock} defaultOpen>
                      <ChangePasswordSection showToast={showToast}/>
                    </Accordion>

                    {/* 2FA — UI only for now */}
                    <Accordion title="Two-Factor Authentication" subtitle="Add an extra layer of security"
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} icon={FiShield} accentOpen={T.olive}>
                      {[
                        {icon:FiSmartphone,title:"Authenticator App",sub:"Not configured",action:"Enable"},
                        {icon:FiMail,title:"Email OTP",sub:"Not configured",action:"Enable"},
                        {icon:FiKey,title:"Backup Codes",sub:"Not generated",action:"Generate"},
                      ].map(item=>(
                        <motion.div key={item.title} whileHover={{x:4}}
                          className="st-row flex items-center justify-between px-4 py-3.5 rounded-[16px] mb-2.5 cursor-pointer"
                          style={{border:`1.5px solid ${T.border}`,background:"rgba(255,255,255,0.7)",transition:"border-color 0.22s"}}
                          onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(242,154,12,0.35)"}
                          onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                          <div className="flex items-center gap-3">
                            <item.icon size={20} style={{color:T.muted}}/>
                            <div>
                              <p className="text-[14px] font-bold text-[#1F1F1F]">{item.title}</p>
                              <p className="text-[12px] font-semibold mt-0.5" style={{color:T.light}}>{item.sub}</p>
                            </div>
                          </div>
                          <GhostBtn onClick={()=>showToast("Coming soon")}>{item.action}</GhostBtn>
                        </motion.div>
                      ))}
                    </Accordion>

                    {/* Connected Accounts — UI only */}
                    <Accordion title="Connected Accounts" subtitle="Third-party login providers"
                      iconBg="linear-gradient(135deg,#185FA5,#378ADD)" icon={FiPaperclip} accentOpen="#185FA5">
                      {[
                        {icon:FiGlobe, title:"Google",   sub:"Not connected", action:"Connect"},
                        {icon:FiMonitor,title:"Apple ID", sub:"Not connected", action:"Connect"},
                      ].map(item=>(
                        <motion.div key={item.title} whileHover={{x:4}}
                          className="st-row flex items-center justify-between px-4 py-3.5 rounded-[16px] mb-2.5 cursor-pointer"
                          style={{border:`1.5px solid ${T.border}`,background:"rgba(255,255,255,0.7)",transition:"border-color 0.22s"}}
                          onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(242,154,12,0.35)"}
                          onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
                          <div className="flex items-center gap-3">
                            <item.icon size={20} style={{color:T.muted}}/>
                            <div>
                              <p className="text-[14px] font-bold text-[#1F1F1F]">{item.title}</p>
                              <p className="text-[12px] font-semibold mt-0.5" style={{color:T.light}}>{item.sub}</p>
                            </div>
                          </div>
                          <GhostBtn onClick={()=>showToast("Coming soon")}>{item.action}</GhostBtn>
                        </motion.div>
                      ))}
                    </Accordion>

                    {/* Active Sessions — REAL DATA from API */}
                    <Accordion title="Active Sessions" subtitle="All devices currently logged into your account"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiMonitor}>
                      <ActiveSessionsSection showToast={showToast}/>
                    </Accordion>
                  </>
                )}

                {/* ── PRIVACY ── */}
                {activeTab==="privacy" && (
                  <>
                    <Accordion title="Data & Privacy Controls" subtitle="Who can see your activity and profile"
                      iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} icon={FiEyeOff} defaultOpen accentOpen={T.olive}>
                      {[
                        ["profileVisibility","Profile visibility","Who can see your name, avatar, and level"],
                        ["sessionActivity","Session activity visible","Whether your sessions appear on your public profile"],
                        ["leaderboard","Appear on leaderboard","Show your rank and points in the monthly leaderboard"],
                        ["analytics","Analytics & personalisation","Allow Divyyog to personalise recommendations"],
                        ["thirdParty","Third-party data sharing","Share anonymised usage data with research partners"],
                      ].map(([key,title,sub])=>(
                        <div key={key} className="flex items-center justify-between py-3.5"
                          style={{borderBottom:`1px solid rgba(239,229,216,0.4)`}}>
                          <div className="flex-1 pr-6">
                            <p className="text-[14px] font-bold text-[#1F1F1F]">{title}</p>
                            <p className="text-[12px] mt-0.5" style={{color:T.light}}>{sub}</p>
                          </div>
                          <Toggle on={privacyPrefs[key]} onChange={(val) => handleTogglePrivacyPref(key, val)}/>
                        </div>
                      ))}
                    </Accordion>
                    <Accordion title="Data Management" subtitle="Export or erase your data"
                      iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} icon={FiDatabase}>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-[16px] p-4" style={{background:"rgba(255,255,255,0.8)",border:`1.5px solid ${T.border}`}}>
                          <div className="flex items-center gap-2 mb-2"><FiDownload size={18} style={{color:T.orange}}/><p className="text-[14px] font-bold text-[#1F1F1F]">Export My Data</p></div>
                          <p className="text-[12px] leading-[1.55] mb-4" style={{color:T.muted}}>Download all your data as JSON file.</p>
                          <GhostBtn onClick={handleExportData}><FiDownload size={12}/> Request Export</GhostBtn>
                        </div>
                        <div className="rounded-[16px] p-4" style={{background:"rgba(252,235,235,0.35)",border:"1.5px solid rgba(226,75,74,0.18)"}}>
                          <div className="flex items-center gap-2 mb-2"><FiTrash2 size={18} style={{color:"#E24B4A"}}/><p className="text-[14px] font-bold text-[#1F1F1F]">Erase Activity Data</p></div>
                          <p className="text-[12px] leading-[1.55] mb-4" style={{color:T.muted}}>Remove session history and progress logs.</p>
                          <DangerBtn onClick={handleEraseActivity}><FiTrash2 size={12}/> Erase Activity</DangerBtn>
                        </div>
                      </div>
                    </Accordion>
                  </>
                )}

                {/* ── ACCOUNT ── */}
                {activeTab==="account" && (
                  <>
                    {/* Logout banner */}
                    <div className="st-logout relative overflow-hidden rounded-[22px] p-6 mb-5 flex items-center justify-between"
                      style={{background:"linear-gradient(145deg,#1F1F1F,#2D2D2D)",boxShadow:"0 16px 40px rgba(0,0,0,0.25)"}}>
                      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                        style={{backgroundImage:"radial-gradient(circle,#fff 1px,transparent 0)",backgroundSize:"14px 14px"}}/>
                      <div className="relative">
                        <p className="font-black text-white text-[18px]"
                          style={{fontFamily:"'Poppins',sans-serif",letterSpacing:"-0.4px"}}>Log Out</p>
                        <p className="text-[13px] mt-1" style={{color:"rgba(255,255,255,0.5)"}}>
                          Signed in as <strong style={{color:"rgba(255,255,255,0.8)"}}>{profile?.email || "…"}</strong>
                        </p>
                      </div>
                      <div className="st-actions flex gap-3 relative">
                        <Btn onClick={handleLogout}><FiLogOut size={14}/> Log Out This Device</Btn>
                        <DangerBtn onClick={handleLogoutEverywhere}><FiLogOut size={14}/> Log Out Everywhere</DangerBtn>
                      </div>
                    </div>

                    {/* Deactivate */}
                    <Accordion title="Deactivate Account" subtitle="Pause your account — reactivate anytime"
                      iconBg="linear-gradient(135deg,#E24B4A,#A32D2D)" icon={FiArchive} defaultOpen accentOpen="#E24B4A">
                      <div className="rounded-[16px] p-5 mb-4"
                        style={{background:"rgba(252,235,235,0.4)",border:"1.5px solid rgba(226,75,74,0.2)"}}>
                        <p className="text-[14px] leading-[1.7]" style={{color:T.mid}}>
                          Deactivating will <strong style={{color:T.dark}}>pause all activity</strong>. Your sessions, achievements, and subscription remain intact. Log back in anytime to reactivate.
                        </p>
                      </div>
                      <div className="st-full mb-4 max-w-[280px]">
                        <Field label="Enter your password to confirm">
                          <Inp type="password" placeholder="Your current password"
                            value={deletePwd} onChange={e=>setDeletePwd(e.target.value)}
                            style={{borderColor:"rgba(226,75,74,0.35)"}}/>
                        </Field>
                      </div>
                      <DangerBtn onClick={handleDeactivate} disabled={!deletePwd}>
                        <FiArchive size={13}/> Deactivate My Account
                      </DangerBtn>
                    </Accordion>

                    {/* Delete permanently */}
                    <Accordion title="Delete Account Permanently" subtitle="Irreversible — all data will be erased"
                      iconBg="linear-gradient(135deg,#E24B4A,#791F1F)" icon={FiTrash2} accentOpen="#E24B4A">
                      <div className="rounded-[16px] p-5 mb-4"
                        style={{background:"rgba(252,235,235,0.6)",border:"2px solid rgba(226,75,74,0.3)"}}>
                        <p className="text-[14px] leading-[1.7] mb-4" style={{color:T.mid}}>
                          This action is <strong style={{color:"#E24B4A"}}>permanent and cannot be undone</strong>. The following will be permanently erased:
                        </p>
                        <div className="grid grid-cols-2 gap-1">
                          {["All session history","Goals & achievements","Certificates & badges","Subscription data","Personal profile","Billing & payment info"].map(item=>(
                            <div key={item} className="flex items-center gap-2 text-[12px] py-0.5" style={{color:T.muted}}>
                              <FiX size={12} style={{color:"#E24B4A",flexShrink:0}}/>{item}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-4 max-w-[540px]">
                        <Field label="Type DELETE to confirm">
                          <Inp placeholder="DELETE" value={deleteText}
                            onChange={e=>setDeleteText(e.target.value)}
                            style={{borderColor:"rgba(226,75,74,0.35)"}}/>
                        </Field>
                        <Field label="Enter your password">
                          <Inp type="password" placeholder="Your current password"
                            value={deletePwd} onChange={e=>setDeletePwd(e.target.value)}
                            style={{borderColor:"rgba(226,75,74,0.35)"}}/>
                        </Field>
                      </div>
                      <DangerBtn onClick={handleDelete}
                        disabled={deleteText!=="DELETE" || !deletePwd || deleting}>
                        {deleting
                          ? <><motion.span animate={{rotate:360}} transition={{duration:1,repeat:Infinity,ease:"linear"}}><FiLoader size={13}/></motion.span> Deleting…</>
                          : <><FiTrash2 size={13}/> Permanently Delete My Account</>}
                      </DangerBtn>
                    </Accordion>
                  </>
                )}

              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Activity Log Erasure Confirmation Modal */}
      <AnimatePresence>
        {eraseModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}
            onClick={() => setEraseModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="rounded-[24px] p-6 max-w-sm w-full bg-white shadow-2xl border border-zinc-200 text-center relative"
            >
              <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mx-auto mb-3.5 text-rose-600">
                <FiTrash2 size={22} />
              </div>
              <h3 className="font-black text-[17px] text-zinc-900 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Erase Activity Data?
              </h3>
              <p className="text-[12.5px] text-zinc-600 mb-5 leading-relaxed">
                Are you sure you want to request erasing your activity logs? This action will queue your practice session history and progress metrics for permanent deletion.
              </p>
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setEraseModalOpen(false)}
                  className="flex-1 h-10 rounded-full font-bold text-[12px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-none cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmEraseActivity}
                  className="flex-1 h-10 rounded-full font-bold text-[12px] text-white bg-rose-600 hover:bg-rose-700 border-none cursor-pointer shadow-md"
                >
                  Confirm Erase
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div key={toast.msg}
            initial={{opacity:0,y:20,scale:0.95}} animate={{opacity:1,y:0,scale:1}}
            exit={{opacity:0,y:20}}
            className="st-toast fixed bottom-7 right-7 z-50 flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
            style={{
              background:toast.type==="error"?"linear-gradient(135deg,#E24B4A,#C0392B)":"linear-gradient(135deg,#1F1F1F,#2D2D2D)",
              boxShadow:"0 16px 48px rgba(0,0,0,0.35)", fontFamily:"'Inter',sans-serif"}}>
            {toast.type==="error"
              ?<FiAlertCircle size={17} style={{color:"#FFB3B3"}}/>
              :<FiCheck size={17} style={{color:T.orange}}/>}
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}